import React, { useCallback, useMemo, useState } from 'react'
import './CallBlockingStyle.css'
import { Checkbox, InputField, Modal, Select } from '@enamelai/design-system'
import { useAppSelector } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  BlockedFeatureKey,
  IBlockedUser,
  IReqBlockedUser,
} from '@dataTypes/blocked-user.type'
import {
  createBlockedUser,
  updateBlockedUserById,
} from '@services/api/blocked-user.api'
import { BLOCK_REASON_PRESET_VALUES } from '@constants/Settings'

const FEATURE_LABELS: Record<BlockedFeatureKey, string> = {
  incoming_calls: 'Incoming calls',
  outgoing_calls: 'Outgoing calls',
  sms_chat: 'SMS chat',
  fax: 'Fax',
}

const REASON_OPTIONS: Array<{ label: string; value: string }> =
  BLOCK_REASON_PRESET_VALUES.map((value) => ({ label: value, value }))

function readUserIdFromStorage(): string {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const u = JSON.parse(raw) as { user_id?: string }
    return u.user_id ?? ''
  } catch {
    return ''
  }
}

interface BlockedNumberData {
  number_display: string[]
  reason: string
  features: BlockedFeatureKey[]
}

const LOADING_CONTAINER = 'block-number-modal'

const initialData: BlockedNumberData = {
  number_display: [],
  reason: '',
  features: ['incoming_calls', 'outgoing_calls', 'sms_chat', 'fax'],
}

interface BlockNumberProps {
  show: boolean
  onHide: () => void
  onSuccess?: () => void | Promise<void>
  existingBlockedUsers?: IBlockedUser[]
}

const toDigits = (value: string): string => value.replace(/\D/g, '')

const BlockNumberModal = ({
  show,
  onHide,
  onSuccess,
  existingBlockedUsers = [],
}: BlockNumberProps): React.ReactElement => {
  const [data, setData] = useState<BlockedNumberData>(initialData)
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string
  }>({})
  const selectedClinicId = useAppSelector(
    (s) => s.clinic.selectedClinic?.clinic_id ?? '',
  )
  const clinicId =
    selectedClinicId ||
    (typeof localStorage !== 'undefined'
      ? localStorage.getItem('clinic_id') || ''
      : '')
  const userId = useMemo(() => readUserIdFromStorage(), [])

  const sanitizedNumbers = useMemo(
    () =>
      Array.from(
        new Set(
          data.number_display.map((n) => n.trim()).filter((n) => n.length > 0),
        ),
      ),
    [data.number_display],
  )

  const existingByDigits = useMemo(() => {
    const map = new Map<string, IBlockedUser>()
    for (const row of existingBlockedUsers) {
      const d = toDigits(row.phone_number ?? '')
      if (d.length > 0) map.set(d, row)
    }
    return map
  }, [existingBlockedUsers])

  const fullyCoveredDuplicates = useMemo(
    () =>
      sanitizedNumbers.filter((n) => {
        const d = toDigits(n)
        const existing = d ? existingByDigits.get(d) : undefined
        if (!existing) return false
        const existingFeatures = Array.isArray(existing.blocked_features)
          ? existing.blocked_features
          : []
        return data.features.every((f) => existingFeatures.includes(f))
      }),
    [sanitizedNumbers, existingByDigits, data.features],
  )

  const validateForm = useCallback((): boolean => {
    const errors: { [key: string]: string } = {}
    if (sanitizedNumbers.length === 0) {
      errors.number_display = 'Phone number is required.'
    } else if (
      fullyCoveredDuplicates.length === sanitizedNumbers.length &&
      sanitizedNumbers.length > 0
    ) {
      errors.number_display =
        'Phone number is already blocked. Input another number'
    }
    if (!data.reason.trim()) {
      errors.reason = 'Reason is required.'
    }
    if (data.features.length === 0) {
      errors.features = 'Select atleast 1.'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [data.reason, data.features, sanitizedNumbers, fullyCoveredDuplicates])

  const handleHideModal = useCallback((): void => {
    setData(initialData)
    setValidationErrors({})
    onHide()
  }, [onHide])

  const confirmBlock = useCallback(async () => {
    if (!clinicId || !userId) {
      showToast(createFailedToast('Call blocking', 'Missing clinic or user.'))
      return
    }

    if (!validateForm()) {
      return
    }

    showLoading({ container: LOADING_CONTAINER })
    try {
      const results = await Promise.all(
        sanitizedNumbers.map(async (phone_number) => {
          const digits = toDigits(phone_number)
          const existing = digits ? existingByDigits.get(digits) : undefined

          if (existing?.blocked_user_id) {
            const existingFeatures = Array.isArray(existing.blocked_features)
              ? existing.blocked_features
              : []
            const merged = Array.from(
              new Set<BlockedFeatureKey>([
                ...existingFeatures,
                ...data.features,
              ]),
            )
            if (merged.length === existingFeatures.length) {
              return {
                kind: 'skipped' as const,
                ok: true,
                phone_number,
              }
            }
            const res = await updateBlockedUserById(existing.blocked_user_id, {
              blocked_features: merged,
              reason: data.reason,
            })
            return {
              kind: 'updated' as const,
              ok: res.statusCode === 200,
              phone_number,
            }
          }

          const body: IReqBlockedUser = {
            clinic_id: clinicId,
            contact_id: null,
            id: null,
            phone_number,
            reason: data.reason,
            blocked_features: data.features,
            created_by: userId,
            is_hidden: false,
          }
          const res = await createBlockedUser(body)
          return {
            kind: 'created' as const,
            ok: res.statusCode === 201,
            phone_number,
          }
        }),
      )

      const created = results.filter((r) => r.kind === 'created' && r.ok).length
      const updated = results.filter((r) => r.kind === 'updated' && r.ok).length
      const skipped = results.filter((r) => r.kind === 'skipped').length
      const failed = results.filter((r) => !r.ok).length
      const changed = created + updated

      if (changed > 0) {
        await onSuccess?.()
      }

      if (failed === 0) {
        const parts: string[] = []
        if (created > 0) {
          parts.push(`${created} number${created === 1 ? '' : 's'} blocked`)
        }
        if (updated > 0) {
          parts.push(
            `${updated} existing block${updated === 1 ? '' : 's'} updated`,
          )
        }
        if (skipped > 0) {
          parts.push(`${skipped} already covered`)
        }
        const message =
          parts.length > 0 ? `${parts.join(', ')}.` : 'No changes.'
        showToast(createSuccessToast('Blocked', message))
        handleHideModal()
        setData(initialData)
        setValidationErrors({})
      } else if (changed > 0) {
        showToast(
          createFailedToast(
            'Call blocking',
            `${changed} processed, ${failed} could not be saved.`,
          ),
        )
      } else {
        showToast(
          createFailedToast('Call blocking', 'Could not save blocked numbers.'),
        )
      }
    } catch (e) {
      console.error(e)
      showToast(
        createFailedToast('Call blocking', 'Could not save blocked number.'),
      )
    } finally {
      hideLoading(LOADING_CONTAINER)
    }
  }, [
    clinicId,
    hideLoading,
    showLoading,
    showToast,
    userId,
    data.reason,
    data.features,
    sanitizedNumbers,
    existingByDigits,
    onSuccess,
    handleHideModal,
    validateForm,
  ])

  return (
    <Modal
      open={show}
      onClose={handleHideModal}
      closeOnBackdrop={false}
      size="md"
      className="scp-cbc-block-modal"
      title="Block number"
      primaryLabel="Block"
      primaryDestructive
      secondaryLabel="Cancel"
      onPrimaryClick={() => {
        void confirmBlock()
      }}
      onSecondaryClick={handleHideModal}
    >
      <div className="scp-cbc-block-fields">
        <InputField
          id="scp-cbc-block-number-numbers"
          variant="message"
          fullWidth
          showCounter={false}
          clearable={false}
          label="Phone number(s) to block"
          placeholder={`+1 (916) 555 1112\n+1 (916) 555 1113`}
          value={data.number_display.join('\n')}
          onChange={(e) => {
            setData({
              ...data,
              number_display: e.target.value.split(/\r?\n/),
            })
            validateForm()
          }}
          error={Boolean(validationErrors.number_display)}
          helperText={validationErrors.number_display}
        />
        <Select
          className="scp-cbc-reason-select"
          label="Reason"
          placeholder="Select a reason"
          options={REASON_OPTIONS}
          value={data.reason || undefined}
          error={Boolean(validationErrors.reason)}
          helperText={validationErrors.reason}
          onChange={(value) => {
            setData({
              ...data,
              reason: typeof value === 'string' ? value : '',
            })
            validateForm()
          }}
        />
        <div className="scp-cbc-features">
          <span className="scp-cbc-applies-label">Applies to</span>
          {(Object.keys(FEATURE_LABELS) as BlockedFeatureKey[]).map((key) => (
            <Checkbox
              key={key}
              id={`feat-${key}`}
              label={FEATURE_LABELS[key]}
              checked={data.features.includes(key)}
              onChange={(event) => {
                const checked = event.target.checked
                setData((prev) => ({
                  ...prev,
                  features: checked
                    ? prev.features.includes(key)
                      ? prev.features
                      : [...prev.features, key]
                    : prev.features.filter((feature) => feature !== key),
                }))
              }}
            />
          ))}
          {validationErrors.features ? (
            <span className="hint-text">{validationErrors.features}</span>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export default BlockNumberModal
